import { ApiClientError, TransportError } from '@/lib/http-core';

/**
 * D-FE (R5): 백오피스 전역 예외 표기 정상화.
 *
 * BE 가 가끔 사용자 친화 메시지 대신 raw enum 값 / 미해석 i18n 키
 * (예: `orders.paymentMethod.undefined`) 를 error.message 로 흘려보낸다.
 * 각 화면이 `error.message` 를 그대로 노출하면 운영자에게 그 raw 키가 보인다.
 *
 * 이 유틸은 단일 정본 매핑 지점이다:
 *  1) 알려진 error.code → 한국어 메시지(KNOWN_ERROR_MESSAGES).
 *  2) BE message 가 "사람이 읽을 수 있는" 문장이면 그대로 사용.
 *  3) message 가 raw 키/enum 처럼 보이면(소문자 점표기 / 공백없는 UPPER_SNAKE /
 *     `undefined`·`null` 포함) 숨기고 매핑 또는 fallback 으로 대체.
 *  4) 알 수 없는 코드는 일반 한국어 fallback.
 *
 * 절대 raw enum.name() / 미해석 키 / 클래스명 / 스택트레이스를 화면에 노출하지 않는다.
 */

/** 알려진 BE error.code → 한국어 운영자 메시지. errors.json 네임스페이스와 정합. */
const KNOWN_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // QA-404(settlement #10) — 목표 금액 미정 캠페인 런칭.
  FUNDING_INVALID_GOAL: '목표 금액을 입력해야 런칭할 수 있습니다.',
  // settlement #11 — 펀딩 판매사·아티스트 연결.
  FUNDING_ARTIST_NOT_FOUND: '연결할 아티스트를 찾을 수 없습니다(없거나 보관된 아티스트). 다른 아티스트를 고르거나 연결을 해제해 주세요.',
  FUNDING_VENDOR_MISMATCH: '선택한 아티스트가 이 판매사에 속하지 않습니다. 아티스트를 바꾸거나 연결을 해제해 주세요.',
  FUNDING_ALREADY_CLOSED: '작성중인 캠페인만 수정할 수 있습니다.',
  // 공통/검증
  COMMON_VALIDATION_FAILED: '입력값을 다시 확인해 주세요.',
  VALIDATION_FAILED: '입력값을 다시 확인해 주세요.',
  COMMON_BAD_REQUEST: '요청을 처리할 수 없습니다. 입력값을 확인해 주세요.',
  COMMON_NOT_FOUND: '대상을 찾을 수 없습니다.',
  COMMON_CONFLICT: '이미 처리되었거나 충돌이 발생했습니다.',
  COMMON_FORBIDDEN: '이 작업을 수행할 권한이 없습니다.',
  COMMON_UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  COMMON_INTERNAL_ERROR: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  INTERNAL_SERVER_ERROR: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',

  // 카탈로그/상품
  CATALOG_PRODUCT_NOT_FOUND: '상품을 찾을 수 없습니다.',
  PRODUCT_CODE_DUPLICATE: '이미 사용 중인 상품 코드입니다. 다른 코드를 입력해 주세요.',
  CATALOG_DATA_INTEGRITY: '중복되거나 제약을 위반하는 값이 있습니다. 입력값을 확인해 주세요.',

  // 재고
  STOCK_INSUFFICIENT: '재고가 부족합니다.',
  STOCK_NEGATIVE_FORBIDDEN: '재고를 음수로 만들 수 없습니다.',

  // 주문/결제 (raw 키 누출이 보고된 영역 — 안전 매핑)
  ORDER_NOT_FOUND: '주문을 찾을 수 없습니다.',
  ORDER_INVALID_STATE: '현재 주문 상태에서는 처리할 수 없습니다.',
  ORDER_PAYMENT_METHOD_INVALID: '결제 수단이 올바르지 않습니다.',
  PAYMENT_NOT_FOUND: '결제 내역을 찾을 수 없습니다.',
  PAYMENT_ALREADY_CAPTURED: '이미 결제 완료된 건입니다.',
  PAYMENT_AMOUNT_MISMATCH: '결제 금액이 일치하지 않습니다.',

  // 마일리지/적립금
  MILEAGE_POLICY_NOT_FOUND: '적립 정책을 찾을 수 없습니다.',
  MILEAGE_INSUFFICIENT_BALANCE: '적립금 잔액이 부족합니다.',
};

/** status code → 일반 한국어 fallback (코드 매핑이 없을 때). */
const STATUS_FALLBACK: Readonly<Record<number, string>> = {
  400: '요청을 처리할 수 없습니다. 입력값을 확인해 주세요.',
  401: '로그인이 필요합니다. 다시 로그인해 주세요.',
  403: '이 작업을 수행할 권한이 없습니다.',
  404: '대상을 찾을 수 없습니다.',
  409: '이미 처리되었거나 충돌이 발생했습니다.',
  422: '입력값을 다시 확인해 주세요.',
  429: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  500: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  502: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  503: '서비스를 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  504: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.',
};

const DEFAULT_FALLBACK = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

/**
 * BE message 가 사용자에게 보여주면 안 되는 raw 키/enum 인지 판별.
 * - 소문자 점표기 i18n 키: `orders.paymentMethod.undefined`
 * - 공백 없는 UPPER_SNAKE enum/code: `PAYMENT_METHOD_UNDEFINED`
 * - `undefined` / `null` 토큰 포함
 * - 자바 클래스명 흔적: `Exception` 포함 + 공백 없음
 * 한글이나 공백이 포함된 정상 문장은 통과시킨다.
 */
export const looksLikeRawKey = (message: string): boolean => {
  const m = message.trim();
  if (m.length === 0) return true;
  // 한글이 하나라도 있으면 사람이 읽는 문장으로 간주.
  if (/[가-힣]/.test(m)) return false;
  // i18n 키 모양: dot 으로 구분된 토큰, 공백 없음 (예: a.b.c)
  if (/^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9_]+)+$/.test(m)) return true;
  // UPPER_SNAKE 코드 모양 (공백 없이 대문자+underscore)
  if (/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(m)) return true;
  // undefined / null 단독 토큰 포함 (공백으로 분리된 어떤 토큰이든)
  if (/(^|[\s.])(undefined|null)([\s.]|$)/.test(m)) return true;
  // 자바 예외 클래스명 흔적
  if (/Exception(:|\b)/.test(m) && !m.includes(' ')) return true;
  return false;
};

/**
 * 어떤 에러든 운영자용 한국어 메시지로 변환. raw 키/enum 노출 금지.
 *
 * @param error  unknown (mutation/query error, ApiClientError, Error, …)
 * @param fallback  컨텍스트별 기본 메시지(예: '상품 저장에 실패했습니다.'). 미지정 시 일반 fallback.
 */
export const friendlyMessage = (error: unknown, fallback?: string): string => {
  const fb = fallback ?? DEFAULT_FALLBACK;

  if (error instanceof ApiClientError) {
    // 1) 알려진 코드 우선.
    const mapped = KNOWN_ERROR_MESSAGES[error.code];
    if (mapped) return mapped;
    // 2) BE message 가 사람이 읽는 문장이면 사용.
    if (error.message && !looksLikeRawKey(error.message)) return error.message;
    // 3) status fallback → 4) 컨텍스트 fallback.
    return STATUS_FALLBACK[error.status] ?? fb;
  }

  if (error instanceof TransportError) {
    return '네트워크 연결을 확인해 주세요.';
  }

  if (error instanceof Error) {
    // 일반 Error 의 message 도 raw 키일 수 있으므로 동일 검사.
    if (error.message && !looksLikeRawKey(error.message)) return error.message;
    return fb;
  }

  return fb;
};
